import { CodeBlock } from '@ui/pages/onboarding/components/configuration/CodeBlock'

import { DATABLE_API_KEY } from './constants'
import { ConfigurationProps } from './types'

export const FluentbitConfiguration = ({ datableApiKey, instanceUrl }: ConfigurationProps) => (
  <>
    <h1 className="my-4 text-3xl font-bold">Configuring using Fluentbit</h1>

    <p className="my-4 text-gray-500">
      Datable supports ingest from JSON HTTP POSTs, which is supported by Fluent Bit and other log forwarders. See the{' '}
      <a
        href="https://docs.fluentbit.io/manual/installation/getting-started-with-fluent-bit"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        Fluent Bit Getting Started
      </a>{' '}
      documentation for options and resources.
    </p>

    <h2 className="my-4 text-2xl font-bold">Configuring Fluent Bit to send Fluent format</h2>

    <p className="my-4 text-gray-500">
      Add following snippet to your{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">fluent-bit.conf</code> file to match any log
      data flowing through fluent and send it to Datable without impacting any existing configurations:
    </p>

    <CodeBlock
      code={`
[OUTPUT]
    Name http
    Match *
    URI /v1/logs/fluent
    Host ${instanceUrl}
    Port 5201
    tls On
    Format json
    Json_date_key    timestamp
    Json_date_format iso8601
    compress gzip
    ${datableApiKey ? `Header  ${DATABLE_API_KEY} ${datableApiKey}` : ''}
`}
    />

    <p className="my-4 text-gray-500">
      Fluent uses a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">log</code> key to store the
      message payload. Datable assume a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">log</code> and
      a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">timestamp</code> key, where{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">log</code> can be a string or a complex JSON
      object.
    </p>

    <div className="my-4 w-full text-gray-500">
      <div className="flex font-bold odd:bg-gray-50">
        <div className="flex-1">Fluent name</div>
        <div className="flex-1">Datable name</div>
        <div className="flex-1">Accepted values</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">log</div>
        <div className="flex-1">body</div>
        <div className="flex-1">string, json, null</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">timestamp</div>
        <div className="flex-1">timestamp</div>
        <div className="flex-1">ISO8601, null</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">
          <em>
            _any key_ (e.g. <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">foo</code>)
          </em>
        </div>
        <div className="flex-1">attributes['foo']</div>
        <div className="flex-1">string, json, null</div>
      </div>
    </div>

    <h3 className="my-4 text-xl font-bold">Full example</h3>

    <p className="my-4 text-gray-500">This example tails a file and sends logs every second</p>

    <CodeBlock
      code={`
[SERVICE]
    log_level    info
    flush           1

[INPUT]
    Name              tail
    Path              /example/path/to/log/file.log
    Refresh_Interval  5
    Rotate_Wait       5
    Mem_Buf_Limit     5MB
    Skip_Long_Lines   On

[OUTPUT]
    Name http
    Match *
    URI /v1/logs/fluent
    Host ${instanceUrl}
    Port 5201
    tls On
    Format json
    Json_date_key    timestamp
    Json_date_format iso8601
    compress gzip
    ${datableApiKey ? `Header  ${DATABLE_API_KEY} ${datableApiKey}` : ''}
`}
    />

    <h2 className="my-4 text-2xl font-bold">Configuring Fluent Bit to send JSON format</h2>

    <p className="my-4 text-gray-500">
      The <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">/v1/logs/fluent</code> endpoint is set up to
      receive preprocessed JSON logs. If you already have structured logs and are using Fluent to turn flattened stdout
      logs into JSON (e.g. AWS ECS Fargate sidecar logs), you'll have a parser configured like this:
    </p>

    <CodeBlock
      code={`
[PARSER]
    Name         docker
    Format       json
    Time_Key     timestamp
    Time_Format  %Y-%m-%dT%H:%M:%S.%L
    Time_Keep    On
    Decode_Field_As    json     log%
`}
    />

    <p className="my-4 text-gray-500">
      In which case, we assume a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">timestamp</code> and
      any number of other top level keys.
    </p>

    <div className="my-4 w-full text-gray-500">
      <div className="flex font-bold odd:bg-gray-50">
        <div className="flex-1">Fluent name</div>
        <div className="flex-1">Datable name</div>
        <div className="flex-1">Accepted values</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">body</div>
        <div className="flex-1">body</div>
        <div className="flex-1">string, json, null</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">message</div>
        <div className="flex-1">body</div>
        <div className="flex-1">string, json, null</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">timestamp</div>
        <div className="flex-1">timestamp</div>
        <div className="flex-1">ISO8601, null</div>
      </div>
      <div className="flex odd:bg-gray-50">
        <div className="flex-1">
          _any key_ (e.g. <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">foo</code>)
        </div>
        <div className="flex-1">attributes['foo']</div>
        <div className="flex-1">string, json, null</div>
      </div>
    </div>

    <p className="my-4 text-gray-500">
      Please use the <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">/v1/logs/json</code> endpoint as
      below:
    </p>

    <CodeBlock
      code={`
[OUTPUT]
    Name http
    Match *
    URI /v1/logs/json
    Host ${instanceUrl}
    Port 5201
    tls On
    Format json
    Json_date_key    timestamp
    Json_date_format iso8601
    compress gzip
    ${datableApiKey ? `Header  ${DATABLE_API_KEY} ${datableApiKey}` : ''}
`}
    />

    <h2 className="my-4 text-2xl font-bold">After configuration</h2>

    <p className="my-4 text-gray-500">
      After changes to <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">fluent-bit.conf</code>, restart
      fluentbit. Logs should be visible in{' '}
      <a href="https://app.datable.io/telemetry" className="text-blue-500 underline hover:text-blue-700">
        the Datable Telemetry
      </a>{' '}
      page within seconds. Logs generated before this configuration change have already been processed by fluent, and
      will not be sent to Datable.
    </p>

    <p className="my-4 text-gray-500">
      If you're experiencing problems, explore the{' '}
      <a
        href="https://docs.fluentbit.io/manual/administration/troubleshooting"
        className="text-blue-500 underline hover:text-blue-700">
        Troubleshooting
      </a>
      ,{' '}
      <a
        href="https://docs.fluentbit.io/manual/local-testing/logging-pipeline"
        className="text-blue-500 underline hover:text-blue-700">
        Configuration
      </a>
      , and{' '}
      <a
        href="https://docs.fluentbit.io/manual/local-testing/logging-pipeline"
        className="text-blue-500 underline hover:text-blue-700">
        Running a Logging Pipeline Locally
      </a>{' '}
      sections of the fluentbit website. Start by setting a{' '}
      <a
        href="https://docs.fluentbit.io/manual/administration/configuring-fluent-bit/classic-mode/configuration-file#example-2"
        className="text-blue-500 underline hover:text-blue-700">
        Standard Out output
      </a>
      .
    </p>
  </>
)
